import {Company} from '../../../models/company';
import {CompanyStatusMeta, CompanyStatusTone} from './company-status';
import {StripeInvoice} from '../../../models/stripe-invoice';

export type CompanySubscriptionHealth =
    | 'active'
    | 'expired'
    | 'missing'
    | 'paymentFailed'
    | 'pendingPayment'
    | 'pastDue'
    | 'invalid';

export interface CompanySubscriptionMeta extends CompanyStatusMeta {
    key: CompanySubscriptionHealth;
}

const STRIPE_EXPIRED_STATUSES = new Set(['canceled', 'incomplete_expired', 'unpaid']);
const STRIPE_PAST_DUE_STATUSES = new Set(['past_due', 'incomplete']);
const INVOICE_ISSUE_STATUSES = new Set(['open', 'uncollectible', 'void']);

export function companyHasPlan(company?: Company | null): boolean {
    const service = company?.service;
    return !!((service?.id ?? '').trim() || (service?.name ?? '').trim());
}

export function getCompanySubscriptionMeta(
    company?: Company | null,
    extras?: {invoice?: StripeInvoice | null; raw?: Record<string, unknown> | null},
): CompanySubscriptionMeta {
    const stripeStatus = String(
        company?.stripeSubscriptionStatus
        || extras?.raw?.['stripeSubscriptionStatus']
        || '',
    ).trim().toLowerCase();
    const invoiceStatus = String(extras?.invoice?.status || '').trim().toLowerCase();
    const companyStatus = String(company?.status || '').trim();
    const hadSubscription = !!(
        company?.stripeSubscriptionId
        || extras?.raw?.['stripeSubscriptionId']
        || company?.billedPeriod
    );

    if (STRIPE_EXPIRED_STATUSES.has(stripeStatus)) {
        return {key: 'expired', label: 'Abbonamento scaduto', tone: 'danger'};
    }
    if (STRIPE_PAST_DUE_STATUSES.has(stripeStatus)) {
        return {key: 'pastDue', label: 'Pagamento in ritardo', tone: 'danger'};
    }
    if (companyStatus === 'paymentFailed') {
        return {key: 'paymentFailed', label: 'Pagamento non riuscito', tone: 'danger'};
    }
    if (companyStatus === 'pendingPayment') {
        return {key: 'pendingPayment', label: 'Pagamento in sospeso', tone: 'warning'};
    }
    if (INVOICE_ISSUE_STATUSES.has(invoiceStatus)) {
        return {key: 'pendingPayment', label: 'Pagamento in sospeso', tone: 'warning'};
    }
    if (!companyHasPlan(company)) {
        if (hadSubscription) {
            return {key: 'expired', label: 'Abbonamento scaduto', tone: 'danger'};
        }
        return {key: 'missing', label: 'Nessun abbonamento', tone: 'danger'};
    }

    const serviceStatus = String(company?.service?.status || '').trim().toLowerCase();
    if (serviceStatus && serviceStatus !== 'active') {
        return {key: 'invalid', label: `Abbonamento ${serviceStatus}`, tone: 'warning'};
    }

    return {key: 'active', label: 'Abbonamento attivo', tone: 'success'};
}

export function isCompanySubscriptionInvalid(meta: CompanySubscriptionMeta): boolean {
    return meta.key !== 'active';
}

export function subscriptionToneBadgeClass(tone: CompanyStatusTone): string {
    switch (tone) {
        case 'success':
            return 'badge badge-success';
        case 'warning':
            return 'badge badge-warning';
        case 'danger':
            return 'badge badge-error';
        default:
            return 'badge';
    }
}
